// src/components/Alimentatori.js — redesign 2026-10 (mockup "Strip LED e Alimentatori").
// Filtri, prezzi, scheda tecnica e riga del carrello (codice, descrizione, tipo) sono gli stessi di prima.
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext'; // Importa AuthContext
import '../css/CatalogoPage.css';
import '../css/ShopPage.css';
import successSound from '../assets/success.mp3'; // Make sure the path is correct
import { urlAsset } from './utils';
import { useLanguage } from '../context/LanguageContext';
import {
  formatEuro, PdfButton, QtyStepper, AddButton, AddedToast, ChipGroup, MobileFiltersButton, FiltersSheet, SpecGrid, ProductPhoto,
} from './shopUi';

const extractPowerWatts = (powerValue) => {
  if (!powerValue) return null;
  const normalized = String(powerValue).trim();
  const wattsMatch = normalized.match(/(\d+(?:[.,]\d+)?)\s*W/i);
  if (wattsMatch) return Number(wattsMatch[1].replace(',', '.'));
  const firstNumber = normalized.match(/\d+(?:[.,]\d+)?/);
  return firstNumber ? Number(firstNumber[0].replace(',', '.')) : null;
};

const parseDimmerTags = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const POWER_RANGES = ['1-50W', '51-100W', '101-150W', '151-200W', '>200W'];
const inPowerRange = (al, range) => {
  const power = extractPowerWatts(al.power);
  if (power == null || Number.isNaN(power)) return false;
  switch (range) {
    case '1-50W':
      return power >= 1 && power <= 50;
    case '51-100W':
      return power > 50 && power <= 100;
    case '101-150W':
      return power > 100 && power <= 150;
    case '151-200W':
      return power > 150 && power <= 200;
    case '>200W':
      return power > 200;
    default:
      return true;
  }
};
const matchesAlim = (al, f) =>
  (!f.category || al.category === f.category) &&
  (!f.powerRange || inPowerRange(al, f.powerRange)) &&
  (!f.ipRating || al.ipRating === f.ipRating) &&
  (!f.dimmerType || parseDimmerTags(al.dimmer).includes(f.dimmerType));

// Disegni dei tipi (tutti, tensione costante, corrente costante, dimmer e comandi)
const typeIcon = (d) => (
  <svg viewBox="0 0 46 30" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);
const TYPE_ICONS = {
  '': typeIcon(<><rect x="6" y="5" width="14" height="9" rx="1.5" /><rect x="26" y="5" width="14" height="9" rx="1.5" /><rect x="6" y="17" width="14" height="9" rx="1.5" /><rect x="26" y="17" width="14" height="9" rx="1.5" /></>),
  '24v': typeIcon(<><rect x="5" y="8" width="24" height="14" rx="2" /><path d="M29 12h5M29 18h5M34 15h7" /><path d="M10 18l3-6 3 6M19 12v6" opacity=".7" /></>),
  CC: typeIcon(<><rect x="5" y="8" width="24" height="14" rx="2" /><path d="M29 12h5M29 18h5M34 15h7" /><path d="M11 15h12M20 12l3 3-3 3" opacity=".7" /></>),
  dimmer: typeIcon(<><rect x="15" y="3" width="16" height="24" rx="3" /><circle cx="23" cy="12" r="4" /><path d="M19 21h8" /><path d="M8 10a9 9 0 0 0 0 10M38 10a9 9 0 0 1 0 10" opacity=".6" /></>),
};

const Alimentatori = ({ searchResults, showPrices }) => {
  const { addToCart } = useContext(CartContext);
  const { user } = useContext(AuthContext); // Obtain the user from the context
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [alimentatori, setAlimentatori] = useState([]);
  const [activeFilters, setActiveFilters] = useState({
    category: '',
    powerRange: '',
    ipRating: '',
    dimmerType: ''
  });
  const [quantities, setQuantities] = useState({});
  const [message, setMessage] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const messageTimer = useRef(null);

  useEffect(() => {
    fetch('/api/products/config/alimentatori')
      .then(response => response.json())
      .then(data => {
        const activeProducts = data.filter(alimentatore => alimentatore.active);
        setAlimentatori(activeProducts);
      })
      .catch(error => console.error('Error loading configuration file:', error));
  }, []);

  useEffect(() => () => clearTimeout(messageTimer.current), []);
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSheetOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sheetOpen]);

  // Base: risultati della ricerca (solo attivi) se ci sono, altrimenti tutti gli attivi; poi i filtri
  const baseAlim = searchResults && searchResults.length > 0
    ? searchResults.filter(alimentatore => alimentatore.active)
    : alimentatori;
  const filteredAlimentatori = baseAlim.filter((al) => matchesAlim(al, activeFilters));

  const handleFilterChange = (filterType, filterValue) => {
    let newFilters = { ...activeFilters };
    if (newFilters[filterType] === filterValue) {
      newFilters[filterType] = '';
    } else {
      newFilters[filterType] = filterValue;
    }
    setActiveFilters(newFilters);
  };

  const resetFilters = () => {
    setActiveFilters({
      category: '',
      powerRange: '',
      ipRating: '',
      dimmerType: ''
    });
  };

  const categoryOptions = [...new Set(alimentatori.map(al => al.category).filter(Boolean))];
  const ipOptions = [...new Set(alimentatori.map(al => al.ipRating).filter(Boolean))];
  const dimmerTypeOptions = [...new Set(
    alimentatori
      .flatMap((al) => parseDimmerTags(al.dimmer))
      .filter((value) => value && value.toLowerCase() !== 'no')
  )];

  const formatCategoryLabel = (value) => {
    const labels = {
      '24v': '24V',
      'CC': 'CC',
      'dimmer': 'Dimmer',
      'accessori': t('powerSupplies.otherAccessories')
    };
    return labels[value] || value;
  };
  // Nome esteso del tipo, per le tessere in alto
  const formatCategoryTitle = (value) => {
    const labels = {
      '24v': t('shop.type24v'),
      'CC': t('shop.typeCC'),
      'dimmer': t('shop.typeDimmer'),
      'accessori': t('powerSupplies.otherAccessories')
    };
    return labels[value] || value;
  };

  const formatDimmerLabel = (value) => {
    const labels = {
      'PUSH-DALI': 'Push-Dali',
      'centralina rgbw con telecomando': 'Centralina RGBW con telecomando',
      'solo telecomando monocolore': 'Solo telecomando monocolore',
      'dimmer radio': 'Dimmer radio',
      '1-10V': '1-10V',
      'DALI': 'DALI',
    };
    return labels[value] || value;
  };

  const playSound = () => {
    const audio = new Audio(successSound);
    audio.play();
  };

  const handleAddToCart = (alimentatore, quantity) => {
    // Solo i campi valorizzati: evita righe tipo "Potenza: undefined" (es. telecomandi senza potenza).
    const hasValue = (v) => v !== undefined && v !== null && String(v).trim() !== '';
    const description = [
      [null, alimentatore.name],
      [t('powerSupplies.category'), formatCategoryLabel(alimentatore.category)],
      [t('powerSupplies.power'), alimentatore.power],
      [t('powerSupplies.ipRating'), alimentatore.ipRating],
      [t('powerSupplies.size'), alimentatore.size],
      ...(alimentatore.category === 'CC' ? [[t('powerSupplies.output'), alimentatore.output_voltage]] : []),
    ]
      .filter(([, value]) => hasValue(value))
      .map(([label, value]) => (label ? `${label}: ${value}` : String(value)))
      .join(',\n');

    const item = {
      code: alimentatore.id,
      description: description,
      unitPrice: alimentatore.price,
      quantity: parseInt(quantity, 10),
      productType: 'alimentatore',
      datasheet: alimentatore.datasheet || null,   // 👈 aggiunto
    };

    addToCart(item);
    playSound();
    showMessage(`${t('powerSupplies.addedToCart')} · ${String(alimentatore.name || '').trim()} × ${parseInt(quantity, 10)}`);
  };

  const showMessage = (text) => {
    setMessage(text);
    clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => setMessage(''), 2400);
  };

  const handleDownload = (datasheet) => {
    if (!datasheet) return;
    // urlAsset punta a goooled.it (dominio diverso da b2b.goooled.com): l'attributo
    // download di un <a> viene ignorato cross-origin, quindi apriamo in una nuova
    // scheda per non far navigare via il B2B dalla pagina corrente.
    window.open(urlAsset(datasheet), '_blank', 'noopener,noreferrer');
  };

  const hasFilters = Object.values(activeFilters).some(Boolean);
  const chipGroups = (
    <>
      <ChipGroup
        title={t('powerSupplies.power')}
        options={POWER_RANGES.map((r) => ({ value: r, label: r }))}
        active={activeFilters.powerRange}
        onToggle={(v) => handleFilterChange('powerRange', v)}
      />
      <ChipGroup
        title={t('powerSupplies.ipRating')}
        options={ipOptions.map((ip) => ({ value: ip, label: `IP${ip}` }))}
        active={activeFilters.ipRating}
        onToggle={(v) => handleFilterChange('ipRating', v)}
      />
      {dimmerTypeOptions.length > 0 && (
        <ChipGroup
          title={t('powerSupplies.type')}
          options={dimmerTypeOptions.map((d) => ({ value: d, label: formatDimmerLabel(d) }))}
          active={activeFilters.dimmerType}
          onToggle={(v) => handleFilterChange('dimmerType', v)}
        />
      )}
    </>
  );
  const sheetCount = ['powerRange', 'ipRating', 'dimmerType'].filter((k) => activeFilters[k]).length;
  const typeTiles = ['', ...categoryOptions];

  return (
    <div className="cat sp">
      <section className="cat-band">
        <div className="cat-band-in">
          <div className="cat-head">
            <div>
              <h1>{t('sidebar.powerSupplies')}</h1>
              <p>{t('shop.alimSubtitle')}</p>
            </div>
            <span className="cat-count">{t('catalog.countOf', { n: filteredAlimentatori.length, total: baseAlim.length })}</span>
          </div>

          <div className={`cat-mounts ${typeTiles.length <= 4 ? 'is-4' : 'is-5'}`} role="group" aria-label={t('powerSupplies.category')}>
            {typeTiles.map((category) => {
              const count = baseAlim.filter((al) => matchesAlim(al, { ...activeFilters, category })).length;
              return (
                <button
                  key={category || 'all'}
                  type="button"
                  className="cat-mt"
                  aria-pressed={activeFilters.category === category}
                  onClick={() => (category ? handleFilterChange('category', category) : setActiveFilters((f) => ({ ...f, category: '' })))}
                >
                  {TYPE_ICONS[category] || TYPE_ICONS['']}
                  <span>{category ? formatCategoryTitle(category) : t('catalog.all')}</span>
                  <small>{count}</small>
                </button>
              );
            })}
          </div>

          <div className="cat-fbar">
            {chipGroups}
            <button type="button" className="cat-reset" onClick={resetFilters} disabled={!hasFilters}>
              {t('powerSupplies.resetFilters')}
            </button>
          </div>
          <MobileFiltersButton
            label={t('powerSupplies.filters')}
            count={sheetCount}
            onOpen={() => setSheetOpen(true)}
            onReset={resetFilters}
            resetLabel={t('powerSupplies.resetFilters')}
            hasFilters={hasFilters}
          />
        </div>
      </section>

      <section className="cat-grid-wrap">
        {filteredAlimentatori.length > 0 ? (
          <div className="sp-grid">
            {filteredAlimentatori.map(alimentatore => {
              const discount = user?.discount || 0; // Ottieni lo sconto dell'utente
              const netPrice = alimentatore.price * (1 - discount / 100); // Calcola il prezzo netto

              const dimmerTags = parseDimmerTags(alimentatore.dimmer);
              const hasDimmer = dimmerTags.length > 0 && dimmerTags[0].toLowerCase() !== 'no';
              const quantity = quantities[alimentatore.id] ?? 1;

              return (
                <article key={alimentatore.id} className="sp-card">
                  <div className="sp-photo is-contain">
                    <ProductPhoto src={alimentatore.image ? urlAsset(alimentatore.image) : ''} alt={alimentatore.name} />
                    <span className="sp-tags">
                      <span className="sp-tag" title={formatCategoryTitle(alimentatore.category)}>{formatCategoryLabel(alimentatore.category)}</span>
                      {String(alimentatore.ipRating) === '67' && <span className="sp-tag is-ip67">IP67</span>}
                    </span>
                    {alimentatore.datasheet && (
                      <PdfButton onClick={() => handleDownload(alimentatore.datasheet)} label={t('shop.datasheet')} />
                    )}
                  </div>
                  <div className="sp-body">
                    <div className="sp-head">
                      <strong>{String(alimentatore.name || '').trim()}</strong>
                      {showPrices && (
                        <span className="sp-net">
                          {formatEuro(user ? netPrice : alimentatore.price)}
                          {user && <small>{t('shop.net')}</small>}
                        </span>
                      )}
                    </div>
                    <SpecGrid
                      cols={3}
                      cells={[
                        { label: t('shop.specPower'), value: String(alimentatore.power || '').trim() },
                        { label: 'IP', value: alimentatore.ipRating ? `IP${alimentatore.ipRating}` : '' },
                        { label: t('shop.specSize'), value: alimentatore.size },
                        ...(alimentatore.category === 'CC' ? [{ label: t('shop.specOutput'), value: alimentatore.output_voltage }] : []),
                      ]}
                    />
                    {hasDimmer && (
                      <div className="sp-ctrl">
                        {dimmerTags.map((tag) => <span key={tag}>{formatDimmerLabel(tag)}</span>)}
                      </div>
                    )}
                    <div className="sp-buy">
                      <span className="sp-code">
                        <span>{t('shop.code')} <b>{alimentatore.id}</b></span>
                        {showPrices && (
                          <span>{t('shop.list')} {user ? <s>{formatEuro(alimentatore.price)}</s> : formatEuro(alimentatore.price)}</span>
                        )}
                      </span>
                      <div className="sp-actions">
                        <QtyStepper
                          value={quantity}
                          onChange={(v) => setQuantities((q) => ({ ...q, [alimentatore.id]: v }))}
                          label={t('shop.quantity')}
                        />
                        <AddButton
                          label={t('shop.add')}
                          onClick={() => handleAddToCart(alimentatore, Math.max(1, parseInt(quantity, 10) || 1))}
                        />
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="cat-empty">
            <p>{t('shop.noResults')}</p>
            {hasFilters && <button type="button" className="cat-chip" onClick={resetFilters}>{t('powerSupplies.resetFilters')}</button>}
          </div>
        )}
      </section>

      <FiltersSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={t('powerSupplies.filters')}
        resetLabel={t('powerSupplies.resetFilters')}
        onReset={resetFilters}
        hasFilters={hasFilters}
        showLabel={t('shop.showResults')}
      >
        {chipGroups}
      </FiltersSheet>

      <AddedToast message={message} onGoToCart={() => navigate('/cart')} goToCartLabel={t('shop.goToCart')} />
    </div>
  );
}

export default Alimentatori;
